import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connectSocket } from "../socket";
import SideBar from "./SideBar";

const TSECopilotDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [socket, setSocket] = useState(null);
  
  // Separate message states for each chat box
  const [customerMessage, setCustomerMessage] = useState("");
  const [dashboardMessage, setDashboardMessage] = useState("");
  
  // Separate message lists for each chat box
  const [customerMessages, setCustomerMessages] = useState([]);
  const [dashboardMessages, setDashboardMessages] = useState([]);
  const [currentSession, setCurrentSession] = useState(null); // Store current session info
  
  const customerChatBodyRef = useRef(null);
  const dashboardChatBodyRef = useRef(null);
  
  // Force light theme for TSE Dashboard
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Light theme configuration only - matching ChatWindow colors
  const theme = {
    light: {
      bg: 'bg-white',
      sidebarBg: 'bg-gray-50',
      cardBg: 'bg-white',
      headerBg: 'bg-gray-50',
      contentBg: 'bg-white',
      inputBg: 'bg-white',
      text: 'text-[#253439]',
      textSecondary: 'text-gray-600',
      textMuted: 'text-[#A2A9AF]',
      timestampColor: 'text-gray-400',
      border: 'border-[#06b6d4]',
      borderLight: 'border-gray-200',
      hover: 'hover:bg-gray-50',
      buttonBg: 'bg-white',
      buttonHover: 'hover:bg-gray-50',
      accent: 'bg-[#06b6d4]',
      accentHover: 'hover:bg-cyan-700',
      success: 'bg-green-600',
      warning: 'bg-yellow-600',
      danger: 'bg-red-600',
    }
  };
  
  const currentTheme = theme.light;
  
  // No theme toggle for TSE Dashboard
  const toggleTheme = () => {
    // Disabled - TSE Dashboard is light theme only
  };

  // Navigation handler
  const handleNavigation = (path) => {
    navigate(path);
  };

  // Helper to capitalize names
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  
  //Since we are not passing TSECopilot Live Agent name in messages to socket.py and then ChatWindow, we need to send the Agentname in the first join message.
  const agentName = localStorage.getItem("username") || "TSE Agent";

  // Handle TSE agent joining session
  const handleJoinSession = (sessionData) => {
    console.log("🤝 Joining session:", sessionData);
    localStorage.setItem('CustomerName', sessionData.username);
    if (socket) {
      socket.emit("tseAgentJoinSession", {
        user_sid: sessionData.user_sid,
        session_room: sessionData.session_room,
        //  agent_name: "TSE Agent"
        agent_name: agentName,
      });
    
      // Store current session information for message routing
      setCurrentSession(sessionData);
      
      // Add confirmation message to dashboard
      setDashboardMessages(prev => [...prev, {
        text: `✅ **Joined Session**: Successfully connected to ${capitalize(sessionData.username)}'s session. You can now assist them directly.`,
        sender: "system",
        timestamp: Date.now()
      }]);
    }
  };

  // Socket connection
  useEffect(() => {
    const newSocket = connectSocket();
    setSocket(newSocket);

    newSocket.on("connect", () => {
      console.log("🔌 Connected to server");
      
      // Join ONLY copilot room as TSE agent - NEVER join diagnostic rooms
      console.log("🔌 TSE Agent joining copilot room only...");
      newSocket.emit("joinCopilotRoom", {
        room_id: "copilot_room",
        role: "agent",
        name: "TSE Agent"
      });
      
      // IMPORTANT: Do NOT call joinRoom - this prevents TSE from appearing in diagnostic mode
      console.log("🔌 TSE Agent staying out of diagnostic rooms completely");
    });

    newSocket.on("disconnect", () => {
      console.log("🔌 Disconnected from server");
    });

    // Listen for copilot messages - these go to dashboard
    newSocket.on("copilotMessage", (data) => {
      console.log("🤖 Received copilot message:", data);
      
      // Check if this is a search_rag action that needs to open TSEAgentRag
      if (data.action === 'search_rag' && data.text.includes("Opening TSEAgentRag")) {
        console.log("🔍 Auto-opening TSEAgentRag in new tab for search functionality");
        const tseAgentRagUrl = `${window.location.origin}/TseAgentRag`;
        window.open(tseAgentRagUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      }
      
      // Add auto-trigger notification for detected actions
      if (data.action && data.confidence) {
        const actionName = data.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        setDashboardMessages(prev => [...prev, {
          text: `🔄 **Auto-triggered**: ${actionName} action (confidence: ${data.confidence})`,
          sender: "system",
          timestamp: Date.now()
        }]);
      }
      
      setDashboardMessages(prev => [...prev, {
        text: data.text,
        sender: data.sender || "copilot",
        timestamp: data.timestamp || Date.now()
      }]);
    });

    // Listen for new copilot session notifications
    newSocket.on("newCopilotSession", (data) => {
      console.log("📬 New copilot session available:", data);
      setDashboardMessages(prev => [...prev, {
        text: `🆕 **New Session Available**: ${data.username} has entered copilot mode and is waiting for assistance. Click "Join Session" to connect.`,
        sender: "system",
        timestamp: Date.now(),
        sessionData: data
      }]);
    });

    // Listen for live agent messages (customer messages only)
    newSocket.on("liveAgentMessage", (data) => {
      console.log("📬 Received liveAgentMessage:", data);
      console.log("📬 Sender:", data.sender);
      
      // Only add customer messages to customer chat
      if (data.sender === "user" || data.sender === "customer") {
        setCustomerMessages(prev => [...prev, {
          text: data.text,
          sender: data.sender || "user",
          agentName: data.agentName,
          userName: data.userName,
          timestamp: data.timestamp || Date.now()
        }]);
      }
    });

    // Debug: Log all socket events for troubleshooting
    newSocket.onAny((eventName, ...args) => {
      console.log(`🔍 Socket event: ${eventName}`, args);
    });

    // Handle session disconnection
    newSocket.on("disconnect", () => {
      console.log("🔌 TSE Agent disconnected");
      setCurrentSession(null); // Clear session when disconnected
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format bot message
  const formatBotMessage = (text) => {
    // Enhanced formatting with bold text and better structure
    let formattedText = text;
    
    // Convert **text** to bold
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert numbered lists to proper formatting
    formattedText = formattedText.replace(/^(\d+\.\s+)(.*?)$/gm, '<div class="mb-2"><strong>$1</strong>$2</div>');
    
    // Convert "Name:", "Command:", etc. to bold labels
    formattedText = formattedText.replace(/^(Name|Command|Parsing Logic|Output Example|Documentation|Manual):/gm, '<strong>$1:</strong>');
    
    // Convert "Next Troubleshooting Steps Based on Context:" to bold header
    formattedText = formattedText.replace(/^(Next Troubleshooting Steps Based on Context):/gm, '<strong class="text-lg">$1:</strong>');
    
    // Remove markdown link format [text](url) - keep only the link part
    formattedText = formattedText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$2');
    
    // URL detection and beautification - more precise matching
    // Match only actual URLs, not surrounding text
    const urlRegex = /\b(https?:\/\/[^\s\)]+|api\.artasupport\.com[^\s\)]*)\b/gi;
    formattedText = formattedText.replace(urlRegex, (match) => {
      // Ensure URL has protocol
      let url = match;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      // Create a clean display name
      let displayName = match;
      if (displayName.startsWith('https://')) {
        displayName = displayName.substring(8);
      } else if (displayName.startsWith('http://')) {
        displayName = displayName.substring(7);
      }
      
      // Extract just the filename from the URL path
      let linkText = displayName;
      if (displayName.includes('/')) {
        const pathParts = displayName.split('/');
        const fileName = pathParts[pathParts.length - 1];
        linkText = fileName;
      }
      
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors duration-200">${linkText}</a>`;
    });
    
    // Add line breaks for better readability
    formattedText = formattedText.replace(/\n/g, '<br>');
    
    return formattedText;
  };

  // Send message function for customer chat
  const sendCustomerMessage = () => {
    if (!customerMessage.trim() || !socket) return;

    const newMessage = {
      text: customerMessage,
      sender: "agent",
      timestamp: Date.now()
    };

    // Add message to customer chat state
    setCustomerMessages(prev => [...prev, newMessage]);
    
    // Emit to customer chat system (not copilot)
    console.log("🤖 TSE sending customer message:", customerMessage);
    
    // Use current session info if available, otherwise fallback to "all"
    const targetUserSid = currentSession?.user_sid || "all";
    console.log("🤖 TSE sending to user_sid:", targetUserSid);
    
    socket.emit("agentMessage", { 
      text: customerMessage, 
      user_sid: targetUserSid,  // Send to specific user if session is active
      target: "customer"
    });
    setCustomerMessage("");
    
    // Reset textarea height to normal size after sending
    setTimeout(() => {
      const customerInput = document.querySelector('textarea[placeholder*="Ask follow-up"]');
      if (customerInput) {
        customerInput.style.height = '28px';
      }
    }, 0);
  };

  // Crew Agent integration - now handled by backend through socket
  const detectActionFromMessage = (message) => {
    // This function is now deprecated - the crew agent analysis is handled by the backend
    // We keep it as a fallback but it's not used in the main flow
    console.log("🤖 Frontend action detection deprecated - using backend crew agent");
    return null;
  };

  // Send message function for dashboard
  const sendDashboardMessage = () => {
    if (!dashboardMessage.trim() || !socket) return;

    const newMessage = {
      text: dashboardMessage,
      sender: "agent",
      timestamp: Date.now()
    };

    // Add message to dashboard chat state
    setDashboardMessages(prev => [...prev, newMessage]);
    
    // Send message to backend crew agent for analysis
    console.log("🤖 TSE sending dashboard message to crew agent:", dashboardMessage);
    socket.emit("copilotMessage", { 
      text: dashboardMessage,
      room_id: "copilot_room",
      target: "copilot"
    });
    
    setDashboardMessage("");
  };

  // Handle copilot action buttons
  const handleCopilotAction = (action) => {
    console.log(`🤖 Copilot action triggered: ${action}`);
    
    // Special handling for search_rag action - open TSEAgentRag in new tab
    if (action === 'search_rag') {
      console.log("🔍 Opening TSEAgentRag in new tab for search functionality");
      const tseAgentRagUrl = `${window.location.origin}/TseAgentRag`;
      window.open(tseAgentRagUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      return;
    }
    
    // Add action message to dashboard chat
    const actionMessage = {
      text: `Action triggered: ${action}`,
      sender: "agent",
      timestamp: Date.now()
    };
    
    setDashboardMessages(prev => [...prev, actionMessage]);
    
    // Emit action to backend via socket
    if (socket) {
      socket.emit("copilotAction", {
        action: action,
        room_id: "copilot_room",
        timestamp: Date.now()
      });
    }
  };

  // Handle Enter key for customer chat
  const handleCustomerKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendCustomerMessage();
    }
  };

  // Handle Enter key for dashboard
  const handleDashboardKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendDashboardMessage();
    }
  };

  // Auto-resize textarea
  const autoResizeTextarea = (textarea) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (customerChatBodyRef.current) {
      customerChatBodyRef.current.scrollTop = customerChatBodyRef.current.scrollHeight;
    }
  }, [customerMessages]);

  useEffect(() => {
    if (dashboardChatBodyRef.current) {
      dashboardChatBodyRef.current.scrollTop = dashboardChatBodyRef.current.scrollHeight;
    }
  }, [dashboardMessages]);

  return (
    <div className={`flex min-h-screen h-screen w-full ${currentTheme.bg} overflow-hidden transition-colors duration-300`}>
      {/* Sidebar */}
      <SideBar 
        mode="agent" 
        onLogout={onLogout} 
        isDarkMode={isDarkMode} 
        onToggleTheme={toggleTheme}
        onLinkClick={handleNavigation}
        currentPath={location.pathname}
      />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full">
        {/* Main Content */}
        <div className="flex h-full">
          {/* Chat Pane (Left) - 50% width */}
          <div className="w-1/2 flex flex-col">
            <div className={`flex-1 flex flex-col ${currentTheme.bg} h-full`}>
              {/* Chat Header */}
              <div className={`${currentTheme.headerBg} ${currentTheme.border} border-b px-4 py-3 flex-shrink-0`}>
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className={`text-lg font-semibold ${currentTheme.text}`}>Customer Chat</h2>
                    <p className={`text-sm ${currentTheme.textSecondary}`}>Live conversation with customer</p>
                  </div>
                  {currentSession && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Connected to {capitalize(currentSession.username)}
                      </div>
                      <button
                        onClick={() => {
                          setCurrentSession(null);
                          setCustomerMessages([]);
                          setDashboardMessages(prev => [...prev, {
                            text: "🔌 **Disconnected**: Session ended. You can join a new session when available.",
                            sender: "system",
                            timestamp: Date.now()
                          }]);
                              if (socket) {
                                socket.emit("clear_context_memory", { sessionId: currentSession?.id });
                              }
                        }}
                        className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition"
                        title="Disconnect from session"
                      >
                        Disconnect
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Chat Messages */}
              <div 
                ref={customerChatBodyRef}
                className="flex-1 overflow-y-auto p-4 space-y-4"
              >
                {customerMessages.length === 0 ? (
                  <div className={`text-center ${currentTheme.textSecondary} mt-8`}>
                    <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="mx-auto mb-4 text-gray-400">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <p>Start the conversation by joining a Copilot chat request!</p>
                  </div>
                ) : (
                  customerMessages.map((msg, index) => (
                    <div
                      key={index}
                      className="w-full flex flex-col mb-4 items-start"
                    >
                      <div
                        className={`px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm ${
                          msg.sender === "agent"
                            ? "bg-purple-50 text-purple-900 border-purple-300"
                            : msg.sender === "user"
                              ? "bg-[#06b6d4] text-white border-[#06b6d4]"
                              : "bg-white text-cyan-900 border-cyan-300"
                        }`}
                        style={{ 
                          whiteSpace: "pre-line",
                          fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                          fontSize: "14px"
                        }}
                      >
                        {msg.sender === "agent" || msg.sender === "user" 
                          ? <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                          : <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                        }
                      </div>
                      <div className="mt-1 text-xs text-gray-400 font-medium" style={{maxWidth: '85%'}}>
                        {msg.sender === "agent" 
                          // ? `Chris ${formatTimestamp(msg.timestamp)}`
                          ? `You ${formatTimestamp(msg.timestamp)}`
                          : msg.sender === "user" 
                            ? `${capitalize(localStorage.getItem('CustomerName'))} ${formatTimestamp(msg.timestamp)}`
                            : `Copilot ${formatTimestamp(msg.timestamp)}`
                        }
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Customer Chat Message Input */}
              <div className={`bg-white rounded-xl border shadow-md flex items-start px-6 py-4 mx-4 mb-6 flex-shrink-0 ${
                currentSession ? 'border-[#06b6d4]' : 'border-gray-300'
              }`}>
                <textarea
                  className={`flex-1 bg-transparent border-none outline-none resize-none overflow-y-auto ${
                    currentSession 
                      ? 'text-[#253439] placeholder-[#A2A9AF] focus:ring-0' 
                      : 'text-gray-400 placeholder-gray-400 cursor-not-allowed'
                  }`}
                  style={{ 
                    minHeight: '28px', 
                    maxHeight: '200px', 
                    height: '28px',
                    fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                    fontSize: "14px"
                  }}
                  placeholder={currentSession ? "Ask follow-up, or type a new question..." : "Connect to a session to start chatting..."}
                  value={customerMessage}
                  onChange={(e) => {
                    if (currentSession) {
                      setCustomerMessage(e.target.value);
                      autoResizeTextarea(e.target);
                    }
                  }}
                  onKeyDown={handleCustomerKeyPress}
                  disabled={!currentSession}
                />
                {/* Send Button */}
                <button
                  className={`ml-2 rounded-lg p-2 transition ${
                    currentSession 
                      ? 'bg-[#06b6d4] hover:bg-cyan-700' 
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                  aria-label="Send"
                  onClick={sendCustomerMessage}
                  disabled={!currentSession}
                  type="button"
                >
                  <svg width="20" height="20" fill="none" stroke={currentSession ? "white" : "gray"} strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M22 2 11 13" />
                    <path d="M22 2l-7 20-4-9-9-4z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Copilot Dashboard Pane (Right) - 50% width */}
          <div className="w-1/2 flex flex-col h-full">
            <div className={`${currentTheme.headerBg} ${currentTheme.border} border-b px-4 py-3 flex-shrink-0`}>
              <h2 className={`text-lg font-semibold ${currentTheme.text}`}>Copilot Dashboard</h2>
              <p className={`text-sm ${currentTheme.textSecondary}`}>AI-powered assistance for TSE</p>
            </div>

            {/* Action Buttons */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-wrap gap-2">
                {/* Top Row - 5 buttons */}
                <button
                  onClick={() => handleCopilotAction('find_bugs')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Find Bugs
                </button>
                
                <button
                  onClick={() => handleCopilotAction('find_cases')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4"/>
                    <path d="M21 12c-1 0-2-1-2-2s1-2 2-2 2 1 2 2-1 2-2 2z"/>
                    <path d="M3 12c1 0 2-1 2-2s-1-2-2-2-2 1-2 2 1 2 2 2z"/>
                  </svg>
                  Find Cases
                </button>
                
                <button
                  onClick={() => handleCopilotAction('next_step')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M5 12h14"/>
                    <path d="m12 5 7 7-7 7"/>
                  </svg>
                  Next Step
                </button>
                
                <button
                  onClick={() => handleCopilotAction('possible_causes')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2 2 0 1 1 18 12H2"/>
                  </svg>
                  Possible Causes
                </button>
                
                <button
                  onClick={() => handleCopilotAction('analyze_cmd_output')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9,22 9,12 15,12 15,22"/>
                  </svg>
                  Analyze Cmd Output
                </button>
              </div>
              
              {/* Bottom Row - 1 button */}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleCopilotAction('search_rag')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  Search (RAG)
                </button>
              </div>
            </div>

            {/* Dashboard Messages */}
            <div 
              ref={dashboardChatBodyRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {dashboardMessages.length === 0 ? (
                <div className={`text-center ${currentTheme.textSecondary} mt-8`}>
                  <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="mx-auto mb-4 text-gray-400">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                dashboardMessages.map((msg, index) => {
                  // Check if message should show action buttons
                  const shouldShowActions = msg.sender === "copilot" && 
                    !msg.text.includes("Action triggered:") && 
                    !msg.text.includes("Determining");
                  
                  return (
                    <div
                      key={index}
                      className="w-full flex flex-col mb-4 items-start"
                    >
                      <div
                        className={`px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm ${
                          msg.sender === "agent"
                            ? "bg-[#06b6d4] text-white border-[#06b6d4]"
                            : msg.sender === "user"
                              ? "bg-white text-[#253439] border-gray-200"
                              : "bg-white text-cyan-900 border-cyan-300"
                        }`}
                        style={{ 
                          whiteSpace: "pre-line",
                          fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                          fontSize: "14px"
                        }                        }
                      >
                        {msg.sender === "agent" || msg.sender === "user" 
                          ? <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                          : <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                        }
                      </div>
                      <div className="mt-1 text-xs text-gray-400 font-medium" style={{maxWidth: '85%'}}>
                        {msg.sender === "agent" 
                          ? `Chris ${formatTimestamp(msg.timestamp)}`
                          : msg.sender === "user" 
                            ? `Customer ${formatTimestamp(msg.timestamp)}`
                            : `Copilot ${formatTimestamp(msg.timestamp)}`
                        }
                      </div>
                      
                      {/* Action Icons - Only show for copilot messages that don't contain excluded text */}
                      {shouldShowActions && (
                        <div className="flex gap-2 mt-2 ml-2 relative">
                          {/* Copy Icon */}
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(msg.text);
                              // Optional: Show a brief toast notification
                              console.log("Message copied to clipboard");
                            }}
                            className="p-1.5 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors duration-200"
                            title="Copy message"
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          </button>
                          
                          {/* Send Icon */}
                          <button
                            onClick={() => {
                              // Send the exact message to customer chat
                              const newMessage = {
                                text: msg.text,
                                sender: "agent",
                                timestamp: Date.now()
                              };
                              setCustomerMessages(prev => [...prev, newMessage]);
                              
                              // Emit to customer chat system
                              if (socket) {
                                // Use current session info if available, otherwise fallback to "all"
                                const targetUserSid = currentSession?.user_sid || "all";
                                socket.emit("agentMessage", { 
                                  text: msg.text, 
                                  user_sid: targetUserSid,  // Send to specific user if session is active
                                  target: "customer"
                                });
                              }
                            }}
                            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors duration-200"
                            title="Send to customer"
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M22 2 11 13"/>
                              <path d="M22 2l-7 20-4-9-9-4z"/>
                            </svg>
                          </button>
                          
                          {/* Edit Icon */}
                          <button
                            onClick={() => {
                              // Copy message to customer chat input for editing
                              setCustomerMessage(msg.text);
                              
                              // Use setTimeout to ensure state update happens first
                              setTimeout(() => {
                                const customerInput = document.querySelector('textarea[placeholder*="Ask follow-up"]');
                                if (customerInput) {
                                  // Auto-resize the textarea to fit content
                                  customerInput.style.height = 'auto';
                                  customerInput.style.height = Math.min(customerInput.scrollHeight, 200) + 'px';
                                  
                                  // Focus and set cursor at the end
                                  customerInput.focus();
                                  customerInput.setSelectionRange(customerInput.value.length, customerInput.value.length);
                                  
                                  // Scroll to top of the textarea if it has scrollbar
                                  if (customerInput.scrollHeight > customerInput.clientHeight) {
                                    customerInput.scrollTop = 0;
                                  }
                                }
                              }, 0);
                            }}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                            title="Edit in customer chat"
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                        </div>
                      )}
                      
                      {/* Join Session Button - Show for system messages with session data */}
                      {msg.sender === "system" && msg.sessionData && (
                        <div className="flex gap-2 mt-2 ml-2">
                          <button
                            onClick={() => handleJoinSession(msg.sessionData)}
                            className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white border border-green-600 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                            title="Join this user's session"
                            type="button"
                          >
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                              <polyline points="10,17 15,12 10,7"/>
                              <line x1="15" y1="12" x2="3" y2="12"/>
                            </svg>
                            Join Session
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Dashboard Message Input */}
            <div className="bg-white rounded-xl border border-[#06b6d4] shadow-md flex items-start px-6 py-4 mx-4 mb-6 flex-shrink-0">
              <textarea
                className="flex-1 bg-transparent border-none outline-none text-[#253439] placeholder-[#A2A9AF] focus:ring-0 resize-none overflow-y-auto"
                style={{ 
                  minHeight: '28px', 
                  maxHeight: '200px', 
                  height: '28px',
                  fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                  fontSize: "14px"
                }}
                placeholder="Ask follow-up, or use natural language like 'find bugs', 'next step', 'search documentation'..."
                value={dashboardMessage}
                onChange={(e) => {
                  setDashboardMessage(e.target.value);
                  autoResizeTextarea(e.target);
                }}
                onKeyDown={handleDashboardKeyPress}
              />
              {/* Send Button */}
              <button
                className="ml-2 bg-[#06b6d4] hover:bg-cyan-700 rounded-lg p-2 transition"
                aria-label="Send"
                onClick={sendDashboardMessage}
                type="button"
              >
                <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M22 2 11 13" />
                  <path d="M22 2l-7 20-4-9-9-4z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TSECopilotDashboard; 